import { useState, useEffect } from "react";
import { withSnackbar } from "notistack";
import { Helmet } from "react-helmet";
import { AddFab } from "../../components/common/FabButtuns";
import SearchFilter from "../../components/common/SearchFilter";
import BranchList from "../../components/features/lists/branches";
import BranchForm from "../../components/features/forms/branch";
import { createBranch, updateBranch, deleteBranch } from "./../../api/api";
import Loader from "../../components/common/Loader";
import { v4 as uuidv4 } from "uuid";
import { useDataLists } from "../../store/DataListsContextProvider";
import { setDataList } from "../../store/action-reducers";

const INIT_BRANCH = {
  Name: "",
  Prefix: "",
  Id: "",
};

const Branches = (props) => {
  const [loading, setLoading] = useState(false);
  const [openBranchForm, setOpenBranchForm] = useState(false);
  const [filterValue, setFilterValue] = useState("");
  const { lists, dataListDispatch } = useDataLists();
  const [branches, setBranches] = useState({
    filtered: [],
  });
  const [contextBranch, setContextBranch] = useState({
    current: {
      ...INIT_BRANCH,
      Id: uuidv4(),
    },
    changeType: "ADDITION",
  });

  useEffect(() => {
    if (filterValue) {
      setBranches({
        filtered: getFilteredBranches(filterValue, lists.branches).sort(
          (a, b) => a.Prefix.charCodeAt(0) - b.Prefix.charCodeAt(0)
        ),
      });
    } else {
      setBranches({
        filtered: lists.branches,
      });
    }
  }, [filterValue]);

  const handleSaveBranch = async () => {
    const branchValues = Object.values(contextBranch.current);

    if (branchValues.includes("")) {
      props.enqueueSnackbar("ERROR : Please fill in all fields.", {
        preventDuplicate: true,
        variant: "warning",
      });
      return;
    }

    setLoading(true);

    if (contextBranch.changeType === "ADDITION") {
      const [, err] = await createBranch(contextBranch.current);

      if (!err) {
        let newBranchesList = [...lists.branches, contextBranch.current].sort(
          (a, b) => a.Prefix.charCodeAt(0) - b.Prefix.charCodeAt(0)
        );

        setBranches({filtered:newBranchesList})
        dataListDispatch(setDataList(newBranchesList, "branches"));

        props.enqueueSnackbar("SUCCESS : Branch created.", {
          preventDuplicate: true,
          variant: "success",
        });
      } else {
        console.error("ERROR : ", err);
        props.enqueueSnackbar(`ERROR : ${err.message}`, {
          preventDuplicate: true,
          variant: "error",
        });
      }
    } else {
      const [res, err] = await updateBranch(contextBranch.current);
      if (res) {
        let newBranchesList = [
          ...lists.branches.filter(
            (branch) => branch.Id !== contextBranch.current.Id
          ),
          contextBranch.current,
        ];
        newBranchesList = newBranchesList.sort(
          (a, b) => a.Prefix.charCodeAt(0) - b.Prefix.charCodeAt(0)
        );

        setBranches({filtered:newBranchesList})
        dataListDispatch(setDataList(newBranchesList, "branches"));

        props.enqueueSnackbar("SUCCESS : Branch updated.", {
          preventDuplicate: true,
          variant: "success",
        });
      } else {
        console.error(err);
        props.enqueueSnackbar(`ERROR : ${err.message}`, {
          preventDuplicate: true,
          variant: "error",
        });
      }
    }

    setLoading(false);
    handleCloseBranchForm();
  };

  const handleDeleteBranch = async () => {
    setLoading(true);
    const [_, err] = await deleteBranch(contextBranch.current.Id);
    if (!err) {
      let newBranchesList = lists.branches.filter(
        (user) => user.Id !== contextBranch.current.Id
      );
      newBranchesList = newBranchesList.sort(
        (a, b) => a.Prefix.charCodeAt(0) - b.Prefix.charCodeAt(0)
      );

      setBranches({filtered:newBranchesList})
      dataListDispatch(setDataList(newBranchesList, "branches"));

      props.enqueueSnackbar("SUCCESS : branch deleted.", {
        preventDuplicate: true,
        variant: "success",
      });
      setLoading(false);
    } else {
      console.error(err);
      props.enqueueSnackbar(`ERROR : ${err.message}`, {
        preventDuplicate: true,
        variant: "error",
      });
      setLoading(false);
    }
  };

  const handleOpenBranchForm = (branch) => {
    if (branch.Prefix) {
      setContextBranch({
        current: branch,
        changeType: "EDIT",
      });
    } else {
      setContextBranch({
        current: {
          ...INIT_BRANCH,
          Id: uuidv4(),
        },
        changeType: "ADDITION",
      });
    }
    setOpenBranchForm(true);
  };

  const handleSetContextBranch = (branch) => {
    setContextBranch({
      current: branch,
      changeType: "EDIT",
    });
  };

  const handleBranchDataChange = (e) => {
    setContextBranch({
      ...contextBranch,
      current: {
        ...contextBranch.current,
        [e.target.name]: e.target.value,
      },
    });
  };

  const handleCloseBranchForm = () => {
    setContextBranch({
      current: {
        ...INIT_BRANCH,
        Id: uuidv4(),
      },
      changeType: "ADDITION",
    });
    setOpenBranchForm(false);
  };

  const handleFilterValueChange = (e) => {
    e.preventDefault();
    setFilterValue(e.target.value);
  };

  const getFilteredBranches = (filterString, branches) => {
    const str = filterString.toLowerCase();
    if (!str) return branches;
    else {
      return branches.filter(
        (branch) =>
          branch.Name.toLowerCase().includes(str) ||
          branch.Prefix.toLowerCase().includes(str)
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>Branches</title>
      </Helmet>

      <SearchFilter
        handleChange={handleFilterValueChange}
        val={filterValue}
        placeholder={"Search by branch 'Name' or 'prefix'"}
      />

      {loading ? (
        <Loader />
      ) : (
        <BranchList
          branches={branches.filtered}
          handleChange={handleBranchDataChange}
          handleSetContextBranch={handleSetContextBranch}
          handleOpenBranchForm={handleOpenBranchForm}
          save={handleSaveBranch}
          handleDelete={handleDeleteBranch}
        />
      )}

      {openBranchForm && (
        <BranchForm
          branch={contextBranch.current}
          edit={contextBranch.changeType === "EDIT"}
          close={handleCloseBranchForm}
          save={handleSaveBranch}
          handleChange={handleBranchDataChange}
          loading={loading}
        />
      )}

      <AddFab open={handleOpenBranchForm} />
    </>
  );
};

export default withSnackbar(Branches);
